import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { memberApi } from "../../lib/api";
import { useMemberAuth } from "../../context/MemberAuthContext";
import MemberLayout, { Avatar } from "../../components/MemberLayout";
import { toast } from "../../hooks/use-toast";
import { ArrowLeft, MessageSquare, MapPin, ExternalLink, X, Send } from "lucide-react";

const MemberProfileView = () => {
  const { id } = useParams();
  const { member: me } = useMemberAuth();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [openCompose, setOpenCompose] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    memberApi.get(`/member/directory/${id}`).then(({ data }) => setM(data.member))
      .catch(() => setNotFound(true));
  }, [id]);

  const startConversation = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      const { data } = await memberApi.post('/member/messages/conversations', { recipient_id: id, content: msg });
      toast({ title: 'Message sent' });
      nav(`/member/messages/${data.conversation_id}`);
    } catch (err) {
      toast({ title: 'Could not send', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setSending(false); }
  };

  if (notFound) return (
    <MemberLayout>
      <Link to="/member/directory" className="inline-flex items-center gap-1 text-[13px] link-underline mb-6"><ArrowLeft size={14} /> Back to directory</Link>
      <div className="rounded-lg p-8" style={{ background: '#fbf8ef' }}>Member not found or not in the directory.</div>
    </MemberLayout>
  );
  if (!m) return <MemberLayout><div className="rounded-lg p-8" style={{ background: '#fbf8ef' }}>Loading…</div></MemberLayout>;

  const first = (m.display_name || 'member').split(' ')[0];

  return (
    <MemberLayout>
      <Link to="/member/directory" className="inline-flex items-center gap-1 text-[13px] link-underline mb-6"><ArrowLeft size={14} /> Back to directory</Link>

      <div className="rounded-lg p-6 md:p-10" style={{ background: '#fbf8ef' }}>
        <div className="flex items-start gap-5 flex-wrap">
          <Avatar src={m.avatar_url} name={m.display_name} size={104} />
          <div className="flex-1 min-w-0">
            <h1 className="hero-title text-[32px] md:text-[44px] leading-tight">{m.display_name}</h1>
            {m.headline && <p className="text-[16px] text-[#0d2a20]/80 mt-1">{m.headline}</p>}
            <div className="flex flex-wrap items-center gap-3 mt-3 text-[13px] text-[#0d2a20]/70">
              {m.company && <span>{m.company}</span>}
              {m.industry && <span className="px-2 py-0.5 rounded" style={{ background: '#ece5d3' }}>{m.industry}</span>}
              {m.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {m.location}</span>}
              {m.linkedin && <a href={m.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 link-underline text-[#e0684d]">LinkedIn <ExternalLink size={12} /></a>}
            </div>
          </div>
          {me?.id !== m.id && (
            <button onClick={() => setOpenCompose(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px]"><MessageSquare size={14} /> Message {first}</button>
          )}
        </div>

        {m.bio && (
          <div className="mt-8">
            <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Bio</div>
            <p className="text-[15px] leading-relaxed text-[#0d2a20]/85 whitespace-pre-wrap">{m.bio}</p>
          </div>
        )}

        {m.skills?.length > 0 && (
          <div className="mt-8">
            <div className="section-eyebrow text-[#0d2a20]/60 mb-3">Skills</div>
            <div className="flex flex-wrap gap-2">
              {m.skills.map(s => <span key={s} className="px-3 py-1 rounded-md text-[13px]" style={{ background: '#f4efe4' }}>{s}</span>)}
            </div>
          </div>
        )}

        {m.conversations_topics?.length > 0 && (
          <div className="mt-8">
            <div className="section-eyebrow text-[#0d2a20]/60 mb-3">Conversations {first} is open to</div>
            <div className="flex flex-wrap gap-2">
              {m.conversations_topics.map(t => <span key={t} className="px-3 py-1 rounded-md text-[13px]" style={{ background: '#fde5c7', color: '#8a4a10' }}>{t}</span>)}
            </div>
          </div>
        )}
      </div>

      {openCompose && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: 'rgba(13,42,32,0.55)' }}>
          <div className="w-full max-w-[520px] rounded-lg p-6" style={{ background: '#fbf8ef' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="hero-title text-[22px]">Message {first}</h3>
              <button onClick={() => setOpenCompose(false)}><X size={18} /></button>
            </div>
            <textarea rows={5} value={msg} onChange={e => setMsg(e.target.value)} placeholder={`Hi ${first}, I saw your profile on Vemoye and…`} className="w-full px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} autoFocus />
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setOpenCompose(false)} className="px-4 py-2 rounded-md btn-ghost text-[13px]">Cancel</button>
              <button onClick={startConversation} disabled={sending || !msg.trim()} className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px] disabled:opacity-60"><Send size={13} /> {sending ? 'Sending…' : 'Send message'}</button>
            </div>
          </div>
        </div>
      )}
    </MemberLayout>
  );
};

export default MemberProfileView;
